import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Facebook, Send, Instagram, Link2, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/api";
import { SITE } from "@/config";
import { trackGAEvent } from "@/consent";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

export default function BlogPostPage() {
  const { lang, slug } = useParams();
  const { t } = useLanguage();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <main className="pt-40 pb-32 text-center" data-testid="post-not-found">
        <p className="text-muted2 mb-6">404</p>
        <Link to={`/${lang}/blog`} className="text-terra font-semibold">{t.blog.back}</Link>
      </main>
    );
  if (!post) return <main className="pt-40 pb-32" data-testid="post-loading" />;

  const tr = post.translations?.[lang] || post.translations?.cs || {};
  const url = `${process.env.REACT_APP_BACKEND_URL}/${lang}/blog/${post.slug}`;
  const shareText = encodeURIComponent(tr.title || "");
  const shareUrl = encodeURIComponent(url);
  const copy = () => {
    navigator.clipboard?.writeText(url);
    toast.success(t.blog.copied);
  };

  return (
    <main className="pt-28 pb-20" data-testid="blog-post-page">
      <Seo page="blog" path={`/blog/${post.slug}`} title={tr.title} description={tr.excerpt} ogImage={post.cover_image || undefined} article={{ datePublished: post.created_at }} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <Reveal>
          <Link to={`/${lang}/blog`} className="inline-flex items-center gap-2 text-sm text-muted2 hover:text-terra mb-8" data-testid="post-back-link">
            <ArrowLeft className="w-4 h-4" /> {t.blog.back}
          </Link>
          <p className="text-xs font-mono uppercase tracking-widest text-terra mb-3">{new Date(post.created_at).toLocaleDateString(lang === "cs" ? "cs-CZ" : lang === "ru" ? "ru-RU" : "en-GB", { day: "numeric", month: "long", year: "numeric" })} · {post.read_minutes} {t.blog.min}</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-8" data-testid="post-title">{tr.title}</h1>
        </Reveal>
        {post.cover_image && (
          <Reveal delay={0.1}>
            <img src={post.cover_image} alt={tr.title} className="w-full h-72 sm:h-96 object-cover rounded-2xl border border-line mb-10" data-testid="post-cover" />
          </Reveal>
        )}
        <Reveal delay={0.15}>
          <div className="prose prose-neutral max-w-none" data-testid="post-content">
            {(tr.content || "").split(/\n\s*\n/).map((p, i) => (
              <div key={i} className={i === 0 ? "text-lg sm:text-xl text-ink leading-relaxed mb-8" : "mb-6"}>
                {p.split("\n").map((line, j) => {
                  const isBullet = line.startsWith("- ");
                  const isHead = !isBullet && line.length < 90 && /[?:]\s*$/.test(line);
                  if (isBullet)
                    return (
                      <span key={j} className="flex gap-3 items-start text-base sm:text-lg text-ink/85 leading-relaxed py-1">
                        <Check className="w-5 h-5 text-sage mt-1 shrink-0" />
                        <span>{line.slice(2)}</span>
                      </span>
                    );
                  if (isHead)
                    return (
                      <span key={j} className="block font-serif text-xl sm:text-2xl font-semibold text-ink mb-3">
                        {line}
                      </span>
                    );
                  return (
                    <span key={j} className="block text-base sm:text-lg leading-relaxed text-ink/85">
                      {line}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-12 pt-8 border-t border-line" data-testid="post-share-block">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-muted2 mb-4">{t.blog.share}</p>
            <div className="flex flex-wrap gap-3">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noreferrer" data-testid="blog-share-facebook" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium hover:border-terra hover:text-terra transition-colors">
                <Facebook className="w-4 h-4" /> Facebook
              </a>
              <a href={`https://t.me/share/url?url=${shareUrl}&text=${shareText}`} target="_blank" rel="noreferrer" data-testid="blog-share-telegram" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium hover:border-terra hover:text-terra transition-colors">
                <Send className="w-4 h-4" /> Telegram
              </a>
              <button onClick={copy} data-testid="blog-share-instagram" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium hover:border-terra hover:text-terra transition-colors">
                <Instagram className="w-4 h-4" /> Instagram
              </button>
              <button onClick={copy} data-testid="blog-share-copy" className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium hover:border-terra hover:text-terra transition-colors">
                <Link2 className="w-4 h-4" /> {t.blog.copy}
              </button>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-12 rounded-3xl bg-sage text-cream p-8 sm:p-10 text-center grain relative overflow-hidden" data-testid="post-cta">
            <p className="font-serif text-2xl sm:text-3xl leading-snug">{t.finalCta.title}</p>
            <a href={SITE.calendarUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackGAEvent("booking_click", { language: lang, page_path: window.location.pathname, booking_provider: "TopMedica", link_url: SITE.calendarUrl })} data-testid="post-cta-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-white hover:text-sage-dark">
              {t.finalCta.cta}
            </a>
          </div>
        </Reveal>
      </article>
    </main>
  );
}
